/**
 * Response Builder Documents Repository
 * 
 * Handles rb_documents collection operations.
 * Phase C1 - Data Model Activation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  type Firestore,
} from 'firebase/firestore';

import type {
  RbDocument,
  CreateDocumentInput,
  DocumentStatus,
} from './types/rb';

/**
 * Validate required fields for document creation
 */
function validateCreateInput(input: CreateDocumentInput): void {
  const required = [
    'workspaceId',
    'filename',
    'storagePath',
    'contentType',
    'documentType',
    'jurisdiction',
    'policyTier',
    'createdByUserId',
  ];

  for (const field of required) {
    if (!input[field as keyof CreateDocumentInput]) {
      throw new Error(`[rbDocumentsRepository] Missing required field: ${field}`);
    }
  }
}

/**
 * Create a new document record
 * 
 * @throws if any required field is missing
 */
export async function createDocument(
  db: Firestore,
  documentId: string,
  input: CreateDocumentInput
): Promise<RbDocument> {
  validateCreateInput(input);

  const now = Timestamp.now();
  const docRef = doc(db, 'rb_documents', documentId);

  const document: Omit<RbDocument, 'id'> = {
    workspaceId: input.workspaceId,
    filename: input.filename,
    storagePath: input.storagePath,
    contentType: input.contentType,
    documentType: input.documentType,
    jurisdiction: input.jurisdiction,
    policyTier: input.policyTier,
    status: 'uploaded',
    createdAt: now,
    createdByUserId: input.createdByUserId,
    updatedAt: now,
  };

  await setDoc(docRef, document);

  return {
    id: documentId,
    ...document,
  };
}

/**
 * Get document by ID
 */
export async function getDocument(
  db: Firestore,
  documentId: string
): Promise<RbDocument | null> {
  const docRef = doc(db, 'rb_documents', documentId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as RbDocument;
}

/**
 * List documents for a workspace
 * Ordered by createdAt descending (newest first)
 */
export async function listDocuments(
  db: Firestore,
  workspaceId: string
): Promise<RbDocument[]> {
  const docsRef = collection(db, 'rb_documents');
  const q = query(
    docsRef,
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as RbDocument[];
}

/**
 * Update document status
 */
export async function updateDocumentStatus(
  db: Firestore,
  documentId: string,
  status: DocumentStatus,
  errorMessage?: string
): Promise<void> {
  const docRef = doc(db, 'rb_documents', documentId);

  const updates: Partial<RbDocument> = {
    status,
    updatedAt: Timestamp.now(),
  };

  if (errorMessage !== undefined) {
    updates.errorMessage = errorMessage;
  }

  await updateDoc(docRef, updates);
}

/**
 * Count documents by status for a workspace
 */
export async function countDocumentsByStatus(
  db: Firestore,
  workspaceId: string
): Promise<Record<DocumentStatus, number>> {
  const docs = await listDocuments(db, workspaceId);

  const counts: Record<DocumentStatus, number> = {
    uploaded: 0,
    processing: 0,
    ready: 0,
    failed: 0,
  };

  for (const doc of docs) {
    counts[doc.status]++;
  }

  return counts;
}
